// --------------------------
// Imports
// --------------------------
import { CategoryId } from "../../constants";

import {
  queryByAnnotation,
  isBackgroundReportIncomplete,
  getIncomeAndCashRunwayData,
} from "../../queryHelpers";

import {
  getBaseCategoryLabel,
  getOutcomeBasedLabel,
  getNumericLabel,
} from "./labelHelpers";
import {
  determineAggregateStatus,
  getPendingStatus,
  createDefaultCategoryData,
  getCategoryStatusFromOutcomes,
  getIncompleteBackgroundDetails,
} from "./outcomeHelpers";

export interface CategoryResult {
  status: string;
  label: string;
  details?: string[];
}

// --------------------------
// Category Processors
// --------------------------

/**
 * Process ID verification data for each applicant
 */
export function processIdVerification({ isLumenStillCalculating, lumenRun }) {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  const idVerificationData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["identity-verification"]
  );

  // Set details from outcomes
  idVerificationData.details = details;

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    idVerificationData.status = outcomeStatus;
  }

  // Set basic label based on status
  idVerificationData.label = getBaseCategoryLabel(
    "id",
    idVerificationData.status
  );

  return idVerificationData;
}

/**
 * Process income data from application result
 */
export function processIncome({
  applicationLumenResult,
  isLumenStillCalculating,
  lumenRun,
}): CategoryResult {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  const incomeData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["income"]
  );

  const {
    incomeToRentRatio,
    cashRunway,
    status: statusFromHistory,
  } = getIncomeAndCashRunwayData(applicationLumenResult);

  // Set details from outcomes
  incomeData.details = details;

  // Get the formula name to check for "total" or "average"/"avg"
  const incomeToRentFormulaName = queryByAnnotation(
    applicationLumenResult,
    "income-to-rent"
  );

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    incomeData.status = outcomeStatus;
  } else {
    // Fall back to existing logic
    incomeData.status = statusFromHistory;
  }

  if (incomeToRentRatio) {
    const incomeToRentLabel = getNumericLabel(
      "income-to-rent",
      incomeToRentRatio,
      incomeToRentFormulaName
    );
    incomeData.label = incomeToRentLabel;
    if (cashRunway !== null && cashRunway !== undefined) {
      const cashRunwayLabel = getNumericLabel("cash-runway", cashRunway, null);
      incomeData.label = `${incomeToRentLabel}\n${cashRunwayLabel}`;
    }
  } else {
    incomeData.label = getBaseCategoryLabel("income", incomeData.status);
  }

  return incomeData;
}

/**
 * Process credit history data from applicant results
 */
export function processCreditHistory({
  applicationLumenResult,
  isLumenStillCalculating,
  lumenRun,
}): CategoryResult {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  // First check if background report is incomplete
  if (isBackgroundReportIncomplete(lumenRun)) {
    // Only if incomplete, get the details
    return getIncompleteBackgroundDetails(lumenRun);
  }

  const creditHistoryData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["credit"]
  );

  // Set details from outcomes
  creditHistoryData.details = details;

  // Query both average and total credit scores
  const avgCreditScoreValue = queryByAnnotation(applicationLumenResult, [
    "credit-avg-credit-score",
    "avg-credit-score",
  ]);

  const totalCreditScoreValue = queryByAnnotation(applicationLumenResult, [
    "credit-total-credit-score",
    "total-credit-score",
  ]);

  const creditScoreValue = avgCreditScoreValue ?? totalCreditScoreValue;

  const isUsingAvgScore = avgCreditScoreValue !== undefined;
  const creditScoreType = isUsingAvgScore ? "avg" : "total";

  // Get the formula name based on which credit score type we're using
  const creditScoreFormulaName = queryByAnnotation(
    applicationLumenResult,
    [
      `credit-${creditScoreType}-credit-score`,
      `${creditScoreType}-credit-score`,
    ],
    "formula"
  );

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    creditHistoryData.status = outcomeStatus;
  } else {
    // Fall back to existing logic

    const creditScoreIdealResult = queryByAnnotation(applicationLumenResult, [
      `credit-${creditScoreType}-credit-score-ideal`,
      `${creditScoreType}-credit-score-ideal`,
    ]);

    const creditScoreConditionalResult = queryByAnnotation(
      applicationLumenResult,
      [
        `credit-${creditScoreType}-credit-score-range-conditional`,
        `${creditScoreType}-credit-score-range-conditional`,
      ]
    );

    if (
      creditScoreIdealResult !== undefined ||
      creditScoreConditionalResult !== undefined
    ) {
      if (creditScoreIdealResult) {
        creditHistoryData.status = "pass";
      } else if (creditScoreConditionalResult) {
        creditHistoryData.status = "warn";
      } else {
        creditHistoryData.status = "fail";
      }
    }
  }

  // First try to get outcome-based label
  const outcomeLabel = getOutcomeBasedLabel(
    "credit",
    creditHistoryData.status,
    lumenRun?.outcomes
  );

  // Use outcome-based label if available, otherwise use dynamic or base label
  if (outcomeLabel) {
    creditHistoryData.label = outcomeLabel;
  } else if (
    creditScoreValue !== null &&
    typeof creditScoreValue === "number" &&
    creditHistoryData.status === "pass"
  ) {
    creditHistoryData.label = getNumericLabel(
      "credit",
      creditScoreValue,
      creditScoreFormulaName
    );
  } else {
    creditHistoryData.label = getBaseCategoryLabel(
      "credit",
      creditHistoryData.status
    );
  }

  return creditHistoryData;
}

/**
 * Process rental history data from applicant results
 */
export function processRentalHistory({
  isLumenStillCalculating,
  lumenRun,
}): CategoryResult {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  // First check if background report is incomplete
  if (isBackgroundReportIncomplete(lumenRun)) {
    // Only if incomplete, get the details
    return getIncompleteBackgroundDetails(lumenRun);
  }

  const rentalHistoryData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["rental"]
  );

  // Set details from outcomes
  rentalHistoryData.details = details;

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    rentalHistoryData.status = outcomeStatus;
  }

  // First try to get outcome-based label
  const outcomeLabel = getOutcomeBasedLabel(
    "rental",
    rentalHistoryData.status,
    lumenRun?.outcomes
  );

  // Use outcome-based label if available, otherwise use base label
  if (outcomeLabel) {
    rentalHistoryData.label = outcomeLabel;
  } else {
    rentalHistoryData.label = getBaseCategoryLabel(
      "rental",
      rentalHistoryData.status
    );
  }

  return rentalHistoryData;
}

/**
 * Process criminal history data from applicant results
 */
export function processCriminalHistory({
  applicantLumenResults,
  isLumenStillCalculating,
  lumenRun,
}): CategoryResult {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  // First check if background report is incomplete
  if (isBackgroundReportIncomplete(lumenRun)) {
    // Only if incomplete, get the details
    return getIncompleteBackgroundDetails(lumenRun);
  }

  const criminalHistoryData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["criminal"]
  );

  // Set details from outcomes
  criminalHistoryData.details = details;

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    criminalHistoryData.status = outcomeStatus;
  } else {
    // Fall back to existing logic
    const statuses = [];

    // Check each applicant's lumen result for criminal history
    applicantLumenResults.forEach(({ lumenResult }) => {
      const criminalHistoryResult = queryByAnnotation(lumenResult, [
        "criminal-history",
        "background-check",
      ]);

      if (criminalHistoryResult !== undefined) {
        statuses.push(criminalHistoryResult ? "pass" : "fail");
      }
    });

    // Determine aggregate status from all applicants
    if (statuses.length > 0) {
      criminalHistoryData.status = determineAggregateStatus(statuses);
    }
  }

  criminalHistoryData.label = getBaseCategoryLabel(
    "criminal",
    criminalHistoryData.status
  );

  return criminalHistoryData;
}

// --------------------------
// Exports
// --------------------------

/**
 * Map of category processors by ID
 */
export const categoryProcessorMap = {
  [CategoryId.ID_VERIFICATION]: processIdVerification,
  [CategoryId.INCOME]: processIncome,
  [CategoryId.CREDIT_HISTORY]: processCreditHistory,
  [CategoryId.RENTAL_HISTORY]: processRentalHistory,
  [CategoryId.CRIMINAL_HISTORY]: processCriminalHistory,
};
